import { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import type { CustomerRefundFormValues } from '@/lib/schemas/customer-refund-schemas'
import { createCustomerRefundSchema } from '@/lib/schemas/customer-refund-schemas'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { MoneyPreview } from '@/components/ui/money-preview'
import { Textarea } from '@/components/ui/textarea'
import { useCustomerRefund } from '@/hooks/use-sales-return-query'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { todayYMD } from '@/lib/date-presets'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

interface Props {
    customerId: string
    /** Tiền dư của khách (= −current_debt), > 0. */
    credit: number
    open: boolean
    onOpenChange: (open: boolean) => void
}

/**
 * Hoàn tiền dư cho khách (M-44): phiếu chi trả lại tiền mặt / chuyển khoản,
 * không gắn đơn. Sổ công nợ ghi dòng "Hoàn tiền" +số tiền, số dư về gần 0.
 */
export function CustomerRefundDialog({
    customerId,
    credit,
    open,
    onOpenChange,
}: Props) {
    const refund = useCustomerRefund(customerId)
    const workflow = useWorkflowTracking('customer_refund', {
        active: open,
        attemptKey: customerId,
        operation: 'create',
    })
    const form = useForm<CustomerRefundFormValues>({
        resolver: zodResolver(createCustomerRefundSchema(credit)),
        defaultValues: {
            amount: '',
            payment_method: '',
            occurred_at: todayYMD(),
            note: '',
        },
    })
    const { register, reset, handleSubmit, control, watch } = form

    useEffect(() => {
        if (open) {
            reset({
                amount: String(credit),
                payment_method: '',
                occurred_at: todayYMD(),
                note: '',
            })
        }
    }, [open, credit, reset])

    const onSubmit = handleSubmit(async (v) => {
        await workflow.run(
            () =>
                refund.mutateAsync({
                    amount: Number(v.amount),
                    payment_method: v.payment_method as
                        'cash' | 'bank_transfer',
                    occurred_at: v.occurred_at,
                    note: v.note.trim() || null,
                }),
            (result) => result.data?.id,
        )
        onOpenChange(false)
    })

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{m.customer_refund_title()}</DialogTitle>
                    <DialogDescription>
                        {m.customer_refund_desc({ amount: formatVnd(credit) })}
                    </DialogDescription>
                </DialogHeader>
                <FormProvider {...form}>
                    <form noValidate onSubmit={onSubmit} className="space-y-4">
                        <Field
                            name="amount"
                            label={m.refund_amount_label()}
                            required
                        >
                            <Input
                                id="amount"
                                type="number"
                                inputMode="numeric"
                                aria-describedby="customer-refund-amount-preview"
                                {...register('amount')}
                            />
                            <MoneyPreview
                                id="customer-refund-amount-preview"
                                value={watch('amount')}
                            />
                        </Field>
                        <Field
                            name="payment_method"
                            label={m.refund_method_label()}
                            required
                        >
                            <Controller
                                control={control}
                                name="payment_method"
                                render={({ field }) => (
                                    <div
                                        role="radiogroup"
                                        aria-label={m.refund_method_label()}
                                        className="flex gap-2"
                                    >
                                        {(
                                            ['cash', 'bank_transfer'] as const
                                        ).map((method) => (
                                            <label
                                                key={method}
                                                className={cn(
                                                    'flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm',
                                                    field.value === method &&
                                                        'border-primary bg-primary/5',
                                                )}
                                            >
                                                <input
                                                    type="radio"
                                                    name={field.name}
                                                    className="h-4 w-4"
                                                    checked={
                                                        field.value === method
                                                    }
                                                    onChange={() =>
                                                        field.onChange(method)
                                                    }
                                                />
                                                {method === 'cash'
                                                    ? m.payment_method_cash()
                                                    : m.payment_method_bank()}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            />
                        </Field>
                        <Field
                            name="occurred_at"
                            label={m.customer_refund_date_label()}
                            required
                        >
                            <Input
                                id="occurred_at"
                                type="date"
                                {...register('occurred_at')}
                            />
                        </Field>
                        <Field name="note" label={m.sales_return_note_label()}>
                            <Textarea
                                id="note"
                                rows={2}
                                {...register('note')}
                            />
                        </Field>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                {m.common_close()}
                            </Button>
                            <Button type="submit" disabled={refund.isPending}>
                                {m.customer_refund_submit()}
                            </Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}
